import { type FC } from "react";

import { Colors } from "@snapchat/snap-design-system";
import { RocketIcon, Sizes } from "@snapchat/snap-design-system-icons";
import { useIntl } from "react-intl";

import { ButtonLink, Flexbox, LabelWithTooltip, NewTag } from "../../../shared/components";
import { useToggleState } from "../../../shared/hooks";
import { GameLensState } from "../../hooks";
import { GameLensCriteriaModal } from "../shared";

import styles from "./GameLensSidebarPanel.module.css";
import { SidebarPanelStyle } from "./LensSidebar.styles";
import {
    GameLensCriteriaLinkText,
    GameLensDescription,
    GameLensLearnMore,
    GameLensNotApprovedDescription,
    GameLensSubmittedDescription,
    GameLensTooltip,
    messages,
} from "./messages.generated";

interface GameLensSidebarPanelProps {
    gameLensState: GameLensState;
}

export const GameLensSidebarPanel: FC<GameLensSidebarPanelProps> = ({ gameLensState }) => {
    const intl = useIntl();
    const [gameLensCriteriaModalProps, showGameLensCriteriaModal] = useToggleState();
    const criteriaLink = (
        <ButtonLink className={styles.learnMoreButton} onClick={showGameLensCriteriaModal}>
            <GameLensCriteriaLinkText />
        </ButtonLink>
    );

    const contentByState = {
        [GameLensState.Submitted]: (
            <p className={styles.descriptionText}>
                <GameLensSubmittedDescription criteriaLink={criteriaLink} />
            </p>
        ),
        [GameLensState.GameApproved]: (
            <>
                <p className={styles.descriptionText}>
                    <GameLensDescription />
                </p>
                <ButtonLink className={styles.learnMoreButton} onClick={showGameLensCriteriaModal}>
                    <GameLensLearnMore />
                </ButtonLink>
            </>
        ),
        [GameLensState.GameNotApproved]: (
            <>
                <p className={styles.descriptionText}>
                    <GameLensNotApprovedDescription />
                </p>
                <ButtonLink className={styles.learnMoreButton} onClick={showGameLensCriteriaModal}>
                    <GameLensLearnMore />
                </ButtonLink>
            </>
        ),
    };

    return (
        <div className={SidebarPanelStyle} data-testid="game-lens-panel">
            <Flexbox gap="8px" flexDirection="column" alignItems="flex-start">
                <LabelWithTooltip
                    dataTestId="sb-game-lens-heading"
                    className={styles.noSpacingBottom}
                    label={intl.formatMessage(messages.gameLensLabel)}
                    content={<GameLensTooltip />}
                />
                <NewTag className={styles.newTag} />
                <Flexbox gap="16px" className={styles.contentRow}>
                    <RocketIcon
                        data-testid="sb-game-lens-icon"
                        className={styles.icon}
                        size={Sizes.Large}
                        fill={Colors.Black.V200}
                    />
                    <Flexbox flexDirection="column" className={styles.textContent}>
                        {contentByState[gameLensState]}
                    </Flexbox>
                </Flexbox>
            </Flexbox>
            <GameLensCriteriaModal modalProps={gameLensCriteriaModalProps} />
        </div>
    );
};
